import type { InspectClass } from '@sb/ai/types';
import { inspectEffectiveThreshold } from '@sb/ai/types/inspectThresholds';

import { blobClassName, type LocateMatch } from './LocateTypes';

export interface InspectCountResult {
  /**
   * One entry per class the skill declares, including classes that saw nothing.
   *
   * Keyed by `InspectClass.id`; the caller turns each into its routine-variable
   * name with `inspectCountVariableKey`.
   */
  countsByClassId: Record<string, number>;
  /**
   * Class names the model emitted that the skill's class list does not
   * describe, deduplicated and sorted.
   *
   * These are counted nowhere, because an unlisted class has no id and so no
   * routine variable to publish under. That makes them the one way this
   * function can undercount, so they are reported for the caller to log rather
   * than dropped silently — see the note on the counting rule below.
   */
  unlistedClassNames: string[];
  /**
   * Detections carrying no class name at all, on either the segment or the
   * detection.
   *
   * `className` is optional in the proto and in `Detection2D`, so this is
   * representable. Reported for the same reason as `unlistedClassNames`: it is
   * a detection the model made that no count reflects.
   */
  unattributedCount: number;
}

/**
 * Detections per defect class, filtered by each class's own threshold.
 *
 * Counting rule, which deliberately differs from `inspectDetectionPasses`:
 * that predicate KEEPS a detection whose class is not in the imported list, so
 * the box stays drawn on the frame with its name on it. Counting cannot do the
 * same, because an unlisted class has no `InspectClass.id` and therefore no
 * `count_<id>` variable to add to. So an unlisted detection is excluded from
 * every count and named in `unlistedClassNames` instead. The same split already
 * exists in the Train panel's own counting.
 *
 * That asymmetry is the one way a defect goes uncounted, and no validation rule
 * can catch it: it happens when the workflow gains a class in Roboflow after
 * the skill imported its list, at which point the step's cached classes and the
 * skill's classes agree with each other and both disagree with the model. The
 * caller is expected to log what comes back in `unlistedClassNames`.
 *
 * Every declared class is present in `countsByClassId` even at zero. A missing
 * key would reach an `If` as an undefined variable, which reads differently
 * from a class that fired nothing.
 */
export function countInspectDetections({
  matches,
  classes,
  workflowFloor,
}: {
  matches: readonly LocateMatch[];
  classes: readonly InspectClass[];
  workflowFloor?: number;
}): InspectCountResult {
  const countsByClassId: Record<string, number> = Object.fromEntries(
    classes.map((defectClass) => [defectClass.id, 0]),
  );

  const unlisted = new Set<string>();
  let unattributedCount = 0;

  for (const match of matches) {
    const className = blobClassName(match);

    if (className === undefined) {
      unattributedCount += 1;
      continue;
    }

    const defectClass = classes.find(
      (candidate) => candidate.label === className,
    );

    if (!defectClass) {
      unlisted.add(className);
      continue;
    }

    // The detection's score, not the segment's. Segmentation refines a box that
    // the detector already scored, so the score the operator tuned against on
    // the class row is this one.
    if (
      match.detection.score <
      inspectEffectiveThreshold(defectClass, workflowFloor)
    ) {
      continue;
    }

    countsByClassId[defectClass.id] += 1;
  }

  return {
    countsByClassId,
    unlistedClassNames: [...unlisted].sort(),
    unattributedCount,
  };
}
